import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ModalGeneric, TextArea } from 'ypf-pas-general-ui-components';
import { useFormik } from 'formik';

import {
  getActionComponents,
  getProgramPoints,
  patchEditAmount
} from '../../../services/workReport';
import { assignmentTypeEnum } from '../../../utils/dataStatus';
import { capitalize, formatDateToSpanish, validateDecimalInput } from '../../../utils/formatUtils';
import { getActionComponentsMonthly } from '../../../services/monthlyServiceRequest';
import { getErrorMessage } from '../../../services/certificationsMaster';
import { Line } from '../../../utils/globalPropTypes';
import EditAmount from '../../InspectExpertReview/EditAmount';
import ErrorComments from '../../InspectExpertReview/ErrorComments';
import LoaderContext from '../../../context/LoaderProvider';
import ProgramPointList from '../ProgramPointModal/ProgramPointList/ProgramPointList';
import useToast from '../../../hooks/useToast';

import './InspectorExpertReviewModal.scss';
import '../WorkReportModals.scss';

const InspectorExpertReviewModal = (props) => {
  const {
    show,
    onHide,
    onAccept,
    positionName,
    lineName,
    um,
    date,
    quantity,
    // Line actualmente es un number, deberia ser un tipo Line
    line,
    asignationType,
    setNewFetch
  } = props;

  const { day, month, year } = formatDateToSpanish(date);
  const [disableButton, setDisableButton] = useState(false);
  const [disabledInputQuantity, setDisabledInputQuantity] = useState(true);
  const [errorMessages, setErrorMessages] = useState([]);
  const { setLoader } = useContext(LoaderContext);
  const [unitMeasure, setUnitMeasure] = useState();
  const [colors, setColors] = useState();
  const [responseText, setResponseText] = useState({
    active: false,
    message: ''
  });
  const [enableEdit, setEnableEdit] = useState(false);
  const [initalProgramPoints, setInitalProgramPoints] = useState([]);
  // const [showAlert, setShowAlert] = useState(false);

  const { setNewToast } = useToast();

  const { values, touched, errors, setFieldValue, setFieldTouched, resetForm } = useFormik({
    initialValues: {
      comment: '',
      quantityForm: quantity,
      programPoints: [],
      expertReview: false,
      action: ''
    }
  });

  const getValueOrDefault = (value, defaultValue = '--') => value ?? defaultValue;

  const handleOnChangeQuantity = async (evt) => {
    if (typeof evt === 'number') {
      await setFieldValue('quantityForm', evt);
    } else if (validateDecimalInput(evt.target.value, 5)) {
      await setFieldValue('quantityForm', evt.target.value);
    }
    setFieldTouched('quantityForm');
    if (typeof evt !== 'number') {
      setFieldTouched('comment');
    }
  };

  const handleOnchangeExpertReview = async (value = undefined) => {
    await setFieldValue('expertReview', typeof value === 'boolean' ? value : !values.expertReview);
    setFieldTouched('expertReview');
  };

  const handleChangeOnColors = (paramColors) => {
    setColors(paramColors);
  };

  const clearForm = async () => {
    setEnableEdit(false);
    resetForm();
    setDisableButton(false);
    setDisabledInputQuantity(true);
    setResponseText({
      active: false,
      message: ''
    });
    setErrorMessages([]);
    setNewFetch((oldValue) => !oldValue);
    onHide();
  };

  const errorMessage = async (lineParam) => {
    try {
      setLoader(true);
      const errorMessagesNew = await getErrorMessage(lineParam?.idParteDiario || lineParam?.id);

      setErrorMessages(errorMessagesNew);
      setLoader(false);
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
      setLoader(false);
    }
  };

  const fetchProgramPoints = async () => {
    try {
      setLoader(true);
      const progamPointsResponse = await getProgramPoints(line?.idParteDiario || line?.id);

      const mappedProgramPoints = progamPointsResponse.map((programPoint) => ({
        quantity: programPoint.cantidad,
        points: programPoint.puntoDePrograma,
        cost: {
          costoClase: getValueOrDefault(programPoint?.claseCosto?.split('-')[1]),
          id: programPoint?.claseCosto,
          nombre: getValueOrDefault(programPoint?.claseCosto),
          label: getValueOrDefault(programPoint?.claseCosto),
          fase: getValueOrDefault(programPoint?.claseCosto?.split('-')[1])
        },
        code: {
          nombre: getValueOrDefault(programPoint?.codigo),
          id: programPoint?.codigo,
          label: getValueOrDefault(programPoint?.codigo)
        },
        subCode: {
          nombre: getValueOrDefault(programPoint?.subcodigo),
          id: programPoint?.subcodigo,
          label: getValueOrDefault(programPoint?.subcodigo)
        },
        detail: programPoint?.descripcion,
        ...programPoint
      }));

      setInitalProgramPoints(mappedProgramPoints);
      setFieldValue('programPoints', mappedProgramPoints);
    } catch (error) {
      setNewToast({
        type: 'ERROR',
        message: error.message
      });
    } finally {
      setLoader(false);
    }
  };

  const getDataMonthly = async () => {
    try {
      setLoader(true);
      const response = await getActionComponentsMonthly(line?.idParteDiario || line?.id);

      if (line?.tienePuntosPrograma) {
        fetchProgramPoints();
      }
      handleOnChangeQuantity(response?.cantidad);
      handleOnchangeExpertReview(response?.revisionExperta);
      setUnitMeasure(response?.unidadMedida);
      handleChangeOnColors(response?.colores);
      setLoader(false);
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
      setLoader(false);
    }
  };

  const getDataForAmount = async () => {
    try {
      setLoader(true);
      const response = await getActionComponents(line?.idParteDiario || line?.id);

      if (line?.tienePuntosPrograma) {
        fetchProgramPoints();
      }

      handleOnChangeQuantity(response?.cantidad);
      handleOnchangeExpertReview(response?.revisionExperta);
      setUnitMeasure(response?.unidadMedida);
      handleChangeOnColors(response?.colores);
      setLoader(false);
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
      setLoader(false);
    }
  };

  const handleClickRectifyButton = () => {
    if (!disabledInputQuantity) {
      setFieldValue('programPoints', initalProgramPoints);
    }
    setDisabledInputQuantity((oldState) => !oldState);
  };

  useEffect(() => {
    if (!line?.tienePuntosPrograma) {
      if (enableEdit && +values.quantityForm > 0 && +values.quantityForm !== quantity) {
        setDisableButton(true);
      } else if (!!Object.values(errors).length || !Object.values(touched).length) {
        setDisableButton(true);
      } else {
        setDisableButton(false);
      }
    } else if (
      (values?.programPoints?.every((programPoint) => programPoint.quantity > 0) &&
        values?.programPoints?.some(
          (programPoint, index) =>
            Number(programPoint.quantity) !== initalProgramPoints[index].quantity
        )) ||
      (values?.programPoints?.every((item) => item.points > 0) &&
        values?.programPoints?.some(
          (programPoint, index) => Number(programPoint.points) !== initalProgramPoints[index].points
        ))
    ) {
      setDisableButton(true);
    } else {
      setDisableButton(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, touched, values.quantityForm, values?.programPoints, values.comment]);

  useEffect(() => {
    if (line && show) {
      errorMessage(line);
      if (asignationType === assignmentTypeEnum.Mensualizada) {
        getDataMonthly();
      } else {
        getDataForAmount();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line]);

  return (
    <ModalGeneric
      backdrop="static"
      infoModal
      onHide={clearForm}
      primaryAction={async () => {
        try {
          setLoader(true);

          const dataPatchExpertReview = {
            id: line?.idParteDiario || line?.id,
            cantidad: line?.tienePuntosPrograma ? 0 : values?.quantityForm,
            revisionExperta: values?.expertReview,
            comentario: values?.comment,
            parteDiarioPuntosPrograma: line?.tienePuntosPrograma
              ? values?.programPoints?.map((workReport) => ({
                  idPartePuntoPrograma: workReport.id,
                  idParteDiario: workReport.idParteTrabajoDiario,
                  puntosDePrograma: workReport.points,
                  cantidad: workReport.quantity
                }))
              : []
          };

          const response = await patchEditAmount(dataPatchExpertReview);

          onHide();
          setNewToast({
            type: 'SUCCESS',
            message: response?.mensaje || response
          });

          onAccept();
        } catch (err) {
          setNewToast({
            type: 'ERROR',
            message: err?.message
          });
        } finally {
          setLoader(false);
          setDisabledInputQuantity(true);
        }
      }}
      primaryActionDisabled={!disableButton || !values?.comment.length}
      primaryActionLabel="Informar revisión"
      secondaryAction={clearForm}
      secondaryActionLabel="Salir"
      show={show}
      size="xl"
      title="Requiere revisión experta"
    >
      <>
        <div className="text-center modal-info">
          <div className="d-flex justify-content-center flex-wrap">
            <span>&quot;{positionName}</span>
            <span>{lineName}</span>
            <span>
              {um} {quantity}&quot;
            </span>
          </div>
          Para el día {capitalize(day?.text)}, {day?.number} de {month} de {year}
        </div>
        {!responseText.active && line ? (
          <>
            {errorMessages.length > 0 && (
              <>
                <hr className="my-4" />
                <div className="w-100">
                  <ul className="container_error_comments">
                    <ErrorComments
                      disableButton={disableButton}
                      errorMessages={errorMessages}
                      setErrorMessages={setErrorMessages}
                      setFieldValue={setFieldValue}
                      setResponseText={setResponseText}
                      value={values?.action}
                    />
                  </ul>
                </div>
              </>
            )}

            <div className="w-100">
              <div className="my-4">
                {line?.tienePuntosPrograma ? (
                  <ProgramPointList
                    disabledInputPP={disabledInputQuantity}
                    disabledInputQuantity={disabledInputQuantity}
                    handleClickRectifyButton={handleClickRectifyButton}
                    line={line}
                    onChangeExpertReview={handleOnchangeExpertReview}
                    setFieldValue={setFieldValue}
                    // showAlert={showAlert}
                    showExpertReview
                    showRectifyQuantityButton
                    values={values}
                    viewCSA
                    viewInputs
                  />
                ) : (
                  <EditAmount
                    colors={colors}
                    enableEdit={enableEdit}
                    handleChangeOnColors={handleChangeOnColors}
                    handleOnChangeQuantity={handleOnChangeQuantity}
                    line={line}
                    onChange={handleOnChangeQuantity}
                    onChangeExpertReview={handleOnchangeExpertReview}
                    setEnableEdit={setEnableEdit}
                    unitMeasure={unitMeasure}
                    value={values?.quantityForm}
                    valueExpertReview={values?.expertReview}
                  />
                )}
              </div>
            </div>

            <div className="my-4">
              <div className="comment-input-container">
                <TextArea
                  errors={errors?.comment}
                  isInvalid={!!errors?.comment}
                  label="Comentario"
                  maxLength={512}
                  name="comment"
                  onChange={async (e) => {
                    await setFieldValue('comment', e.target.value);
                    setFieldTouched('comment');
                  }}
                  placeholder="Motivo de edición"
                  resize={false}
                  value={values?.comment}
                />
              </div>
            </div>
          </>
        ) : (
          <div className="text-center modal-info">
            <hr className="divide-line-bottom" />
            <p className="d-flex justify-content-center flex-wrap m-4">{responseText.message}</p>
          </div>
        )}
      </>
    </ModalGeneric>
  );
};

export default InspectorExpertReviewModal;

InspectorExpertReviewModal.propTypes = {
  assignmentTypeEnum: PropTypes.shape({
    id: PropTypes.number,
    nombre: PropTypes.string
  }),
  date: PropTypes.string,
  line: PropTypes.oneOfType([PropTypes.number, Line]),
  lineName: PropTypes.string,
  onAccept: PropTypes.func,
  onHide: PropTypes.func,
  positionName: PropTypes.string,
  quantity: PropTypes.number,
  show: PropTypes.bool,
  um: PropTypes.string,
  asignationType: PropTypes.number.isRequired,
  setNewFetch: PropTypes.func
};

InspectorExpertReviewModal.defaultProps = {
  assignmentTypeEnum: {
    id: 0,
    nombre: ''
  },
  line: null,
  show: false,
  quantity: null,
  onHide: () => {
    /* Empty */
  },
  onAccept: () => {
    /* Empty */
  },
  date: '',
  lineName: '',
  positionName: '',
  um: '',
  setNewFetch: () => {
    /* Empty */
  }
};
